import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AuthOverlay } from "../../components/common/AuthOverlay/AuthOverlay";
import { useExchangeSamlCodeMutation } from "../../features/auth/auth.api";
import { setCredentials } from "../../features/auth/auth.slice";
import { AuthProvider } from "../../features/auth/auth.types";
import {
	extractAuthErrorMessage,
	parseAuthCallbackFromLocation,
	stripAuthCallbackParamsFromUrl,
} from "../../features/auth/auth.utils";
import { addToast } from "../../features/toast/toast.slice";

export const AuthCallback = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [exchangeSamlCode] = useExchangeSamlCodeMutation();
	const [message, setMessage] = useState("Signing you in…");
	const hasStartedRef = useRef(false);

	useEffect(() => {
		if (hasStartedRef.current) return;
		hasStartedRef.current = true;

		const completeSignIn = async () => {
			const { code, error } = parseAuthCallbackFromLocation(window.location);

			if (error) {
				stripAuthCallbackParamsFromUrl();
				dispatch(
					addToast({
						type: "error",
						message: error || "Sign-in failed. Please try again.",
					}),
				);
				navigate("/login", { replace: true });
				return;
			}

			if (!code) {
				dispatch(
					addToast({
						type: "error",
						message: "Sign-in code missing. Please try again.",
					}),
				);
				navigate("/login", { replace: true });
				return;
			}

			try {
				setMessage("Verifying your account…");
				stripAuthCallbackParamsFromUrl();

				const session = await exchangeSamlCode({ code }).unwrap();
				if (!session.accessToken || !session.user.id) {
					throw new Error("Sign-in succeeded, but session data is incomplete.");
				}

				dispatch(
					setCredentials({
						accessToken: session.accessToken,
						refreshToken: session.refreshToken,
						user: session.user,
						authProvider: AuthProvider.SAML,
					}),
				);
				navigate("/dashboard", { replace: true });
			} catch (err) {
				dispatch(
					addToast({
						type: "error",
						message: extractAuthErrorMessage(err),
					}),
				);
				navigate("/login", { replace: true });
			}
		};

		void completeSignIn();
	}, [dispatch, exchangeSamlCode, navigate]);

	return <AuthOverlay message={message} />;
};
